import { freecell, type FreecellRules } from '@cardengine/shared';
import { useStore } from '../store.js';

interface FreecellRulesEditorProps {
  rules: FreecellRules;
  editable: boolean;
}

const CELL_OPTIONS: { value: number; label: string; hint: string }[] = [
  { value: 1, label: 'One', hint: 'Brutal, and most deals are simply not winnable.' },
  { value: 2, label: 'Two', hint: 'Hard. Emptying a column becomes the whole game.' },
  { value: 3, label: 'Three', hint: 'Noticeably tighter — long runs stop carrying.' },
  { value: 4, label: 'Four', hint: 'Standard. Almost every deal can be solved.' },
  { value: 5, label: 'Five', hint: 'Gentler than standard, if you want the practice.' },
  { value: 6, label: 'Six', hint: 'Very forgiving — a run of seven moves in one go.' },
];

interface ToggleProps {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function Toggle({ label, hint, checked, disabled, onChange }: ToggleProps) {
  return (
    <label className={`rule ${disabled ? 'rule--locked' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="rule__body">
        <span className="rule__label">{label}</span>
        <span className="rule__hint">{hint}</span>
      </span>
    </label>
  );
}

/**
 * FreeCell's settings, in the same shape as the other two editors.
 *
 * There is only really one knob — how many cells — because that is the only
 * thing about FreeCell people genuinely play differently. It reuses the same
 * class names as the Switch and Solitaire editors deliberately, so the lobby
 * looks like one page rather than three that happen to share a route.
 */
export function FreecellRulesEditor({ rules, editable }: FreecellRulesEditorProps) {
  const updateRules = useStore((s) => s.updateRules);

  const set = <K extends keyof FreecellRules>(key: K, value: FreecellRules[K]) => {
    if (editable) updateRules({ [key]: value } as Partial<FreecellRules>);
  };

  const activePreset = freecell.RULE_PRESETS.find(
    (preset) => JSON.stringify(preset.rules) === JSON.stringify(rules),
  );

  return (
    <section className="rules" aria-label="FreeCell settings">
      <header className="rules__header">
        <h2>Settings</h2>
      </header>

      <div className="presets">
        <span className="presets__label">Preset</span>
        <div className="presets__row">
          {freecell.RULE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`presets__button ${activePreset?.id === preset.id ? 'is-active' : ''}`}
              disabled={!editable}
              onClick={() => editable && updateRules(preset.rules)}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
        <p className="presets__description">
          {activePreset ? activePreset.description : 'Custom settings.'}
        </p>
      </div>

      <div className="rules__ranks">
        <label className="rule rule--choice">
          <span className="rule__label">Cells</span>
          <select
            value={rules.freeCells}
            disabled={!editable}
            onChange={(event) => set('freeCells', Number(event.target.value))}
          >
            {CELL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="rule__hint">
            {CELL_OPTIONS.find((o) => o.value === rules.freeCells)?.hint}
          </span>
        </label>
      </div>

      <div className="rules__toggles">
        <Toggle
          label="Move runs in one go"
          hint="Off means shifting a sequence one card at a time, as you would on a real table. On, the game does the shuffling for you — as far as the empty cells allow."
          checked={rules.supermoves}
          disabled={!editable}
          onChange={(value) => set('supermoves', value)}
        />
        <Toggle
          label="Take cards back off the foundations"
          hint="A banked card can come back down to the tableau if it rescues the position."
          checked={rules.allowFoundationToTableau}
          disabled={!editable}
          onChange={(value) => set('allowFoundationToTableau', value)}
        />
      </div>
    </section>
  );
}
